/**
 * DelayedTaskRow Component
 *
 * Displays a delayed task row with status dot, title, assignee avatar,
 * priority badge, due date, and progress bar.
 * Uses the same UI components as the main TaskListRow for visual consistency.
 *
 * @module pages/DashboardPage/components/DelayedTaskRow
 */

import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';

import { STATUS_COLORS, TaskStatus } from '@/types';
import { useLocalizedDate } from '@/hooks';
import { PriorityBadge } from '@/components/ui/PriorityBadge';
import { FormerMemberBadge } from '@/components/common/FormerMemberBadge';
import { UserAvatar } from '@/components/user/UserAvatar';
import { TaskProgressBar } from '@/components/ui/TaskProgressBar';

import type { DelayedTaskRowProps } from './DelayedTaskRow.types';
import styles from './DelayedTaskRow.module.css';

/** Percentage multiplier for progress calculations */
const PERCENTAGE_MULTIPLIER = 100;

/** Em dash character for empty field placeholders */
const EM_DASH = '\u2014';

export const DelayedTaskRow = memo(function DelayedTaskRow({
  task,
}: DelayedTaskRowProps) {
  const { formatDateCompact } = useLocalizedDate();

  const dotStyle = {
    '--dot-color': STATUS_COLORS[TaskStatus.Delayed],
  } as CSSProperties;

  const progressPercentage = useMemo(
    () =>
      task.requirementCount > 0
        ? Math.round(
            (task.completedRequirementCount / task.requirementCount) *
              PERCENTAGE_MULTIPLIER
          )
        : 0,
    [task.completedRequirementCount, task.requirementCount]
  );

  return (
    <div className={styles.row}>
      <span className={styles.dot} style={dotStyle} />
      <span className={styles.taskTitle}>{task.title}</span>
      <div className={styles.assignee}>
        {task.assigneeName ? (
          <>
            <UserAvatar
              displayName={task.assigneeName}
              avatarUrl={task.assigneeAvatarUrl}
              size="sm"
            />
            <span className={styles.assigneeName}>{task.assigneeName}</span>
            {task.assignedToIsFormerMember && <FormerMemberBadge />}
          </>
        ) : (
          <span className={styles.emDash}>{EM_DASH}</span>
        )}
      </div>
      <span className={styles.priorityCol}>
        <PriorityBadge priority={task.priority} variant="inline" />
      </span>
      <span className={styles.dueDate}>
        {task.dueDate ? formatDateCompact(task.dueDate) : EM_DASH}
      </span>
      <span className={styles.location}>{task.location ?? EM_DASH}</span>
      <div className={styles.progressContainer}>
        <TaskProgressBar
          progress={progressPercentage}
          status={TaskStatus.Delayed}
          variant="table"
        />
      </div>
    </div>
  );
});

DelayedTaskRow.displayName = 'DelayedTaskRow';
